<!-- Griffe eines Akkords in EINER Balgrichtung: Knöpfe, Griffschrift-Schnipsel, Bass (optional als Knopfraster), Varianten-Pfeile. Emits play-grip/play-bass. -->
<script setup lang="ts">
import {Icon} from '@iconify/vue'
import {computed, ref, watch} from 'vue'

import {buttonNoteLabel, InstrumentBody} from '@/components/harmonika'
import BassBoard from '@/components/harmonika/BassBoard.vue'
import {type ChordDirectionResult, type Grip, gripButtonIds} from '@/components/harmonika/gripTypes'
import OsmdMeasure from '@/components/measure-selection/OsmdMeasure.vue'
import {extractMeasureAsStandaloneMusicXml} from '@/composables/musicXmlDom'
import type {BassLayoutSettings} from '@/generated'
import {sameMembers} from '@/shared/sameMembers'

import {germanNoteLabel} from './chordFinder'

const {title, isDruck, result, musicXml, firstMeasureIndex, instrumentOffset = 0, bassLayout = null} = defineProps<{
  title: string
  isDruck: boolean
  result: ChordDirectionResult
  /** Griffschrift aller Griffe beider Richtungen, ein Takt je Griff. */
  musicXml: string
  /** Index des Takts, mit dem die Griffe dieser Richtung im `musicXml` beginnen. */
  firstMeasureIndex: number
  /** Halbtöne der eigenen Harmonika über G-C-F-B – nur für die Knopf-Beschriftung. */
  instrumentOffset?: number
  /** Bass-Seite, auf der die Knöpfe der Bass-Silben hervorgehoben werden; `null` = ausgeblendet. */
  bassLayout?: BassLayoutSettings | null
}>()

const emit = defineEmits<{
  'play-grip': [grip: Grip, arpeggio: boolean]
  'play-bass': []
}>()

const variantIndex = ref(0)
// Nur bei anderen Griffen zurück auf Variante 1 – eine Änderung der Bass-Belegung liefert dieselben
// Griffe neu und soll die gewählte Variante nicht verwerfen.
watch(() => JSON.stringify(result.grips), () => {
  variantIndex.value = 0
})

const grip = computed<Grip | null>(() => result.grips[variantIndex.value] ?? null)
const activeButtons = computed<Set<string>>((oldValue) => {
  const ids = gripButtonIds(grip.value)
  return sameMembers(oldValue, ids) ? oldValue : ids
})

// shortcut: Abgleich über den Griffschrift-Namen – zwei Knöpfe mit demselben Namen leuchten gemeinsam
// (die Bass-Zeile wäre dann ohnehin mehrdeutig); eine Klartext-Silbe ohne Knopf trifft keinen.
const activeBassNames = computed<Set<string>>((oldValue) => {
  const names = new Set(result.bass_syllables.filter(Boolean))
  return sameMembers(oldValue, names) ? oldValue : names
})

const snippet = computed(() => {
  if (!grip.value || !musicXml) return null
  const index = firstMeasureIndex + variantIndex.value
  return extractMeasureAsStandaloneMusicXml(musicXml, 0, index + 1, index)
})

function buttonLabel(rowIdx: number, btnIdx: number): string {
  return germanNoteLabel(buttonNoteLabel(rowIdx, btnIdx, isDruck ? 'DRUCK' : 'ZUG', instrumentOffset))
}

function step(delta: number) {
  variantIndex.value = Math.min(Math.max(variantIndex.value + delta, 0), result.grips.length - 1)
}
</script>

<template>
  <section class="flex min-w-0 flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
  <header class="flex items-center justify-between">
    <h3 class="text-lg font-bold tracking-wide" :class="isDruck ? 'text-emerald-700' : 'text-blue-700'">
      {{ title }}
    </h3>
    <div v-if="result.grips.length > 1" class="flex items-center gap-1 text-sm text-gray-600">
      <button
          type="button"
          class="rounded p-1 hover:bg-gray-100 disabled:opacity-30"
          :disabled="variantIndex === 0"
          aria-label="Vorige Variante"
          @click="step(-1)"
      >
        <Icon icon="lucide:chevron-left"/>
      </button>
      <span>Variante {{ variantIndex + 1 }} / {{ result.grips.length }}</span>
      <button
          type="button"
          class="rounded p-1 hover:bg-gray-100 disabled:opacity-30"
          :disabled="variantIndex >= result.grips.length - 1"
          aria-label="Nächste Variante"
          @click="step(1)"
      >
        <Icon icon="lucide:chevron-right"/>
      </button>
    </div>
  </header>

  <p v-if="!grip" class="text-sm text-gray-500">In dieser Balgrichtung nicht spielbar.</p>

  <div class="flex flex-col gap-4 sm:flex-row sm:items-start">
    <!-- Hochkant wie das Instrument in der Hand: schmal genug, dass daneben Platz bleibt. -->
    <InstrumentBody
        v-if="grip"
        vertical
        class="shrink-0 self-center sm:self-start"
        :active-buttons="activeButtons"
        :get-label="buttonLabel"
        :direction="isDruck ? 'DRUCK' : 'ZUG'"
    />

    <div class="flex min-w-0 flex-1 flex-col gap-3">
      <!-- shrink-0: OsmdMeasure ist overflow-hidden, als Flex-Item schrumpfte es sonst unter die
           SVG-Höhe und schnitte die Lyric-Zeilen (Druck-Strich, Bass-Silben) ab. -->
      <OsmdMeasure v-if="snippet" :music-xml="snippet" :zoom="0.7" class="shrink-0"/>

      <p v-if="!result.has_chord_button" class="text-xs text-amber-700">
        Kein Begleitknopf für diesen Akkord – die Akkordtöne klingen einzeln.
      </p>

      <div class="flex flex-wrap gap-2">
        <button
            type="button"
            class="chord-play-button"
            :disabled="!grip"
            title="Akkord zerlegt abspielen"
            @click="grip && emit('play-grip', grip, true)"
        >
          <Icon icon="lucide:audio-lines"/>
          Zerlegt
        </button>
        <button
            type="button"
            class="chord-play-button"
            :disabled="!grip"
            title="Akkord abspielen"
            @click="grip && emit('play-grip', grip, false)"
        >
          <Icon icon="lucide:play"/>
          Akkord
        </button>
        <button
            type="button"
            class="chord-play-button"
            :disabled="!result.bass_midi.some((tones) => tones.length)"
            title="Wechselbass abspielen"
            @click="emit('play-bass')"
        >
          <Icon icon="lucide:drum"/>
          Bass
        </button>
      </div>
    </div>

    <BassBoard
        v-if="bassLayout"
        vertical
        class="shrink-0 self-center sm:self-start"
        :layout="bassLayout"
        :direction="isDruck ? 'druck' : 'zug'"
        :show-sounds="false"
        :active-names="activeBassNames"
    />
  </div>
  </section>
</template>

<style scoped>
@import 'tailwindcss';

.chord-play-button {
  @apply inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1 text-sm
  text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-40;
}
</style>
